/**
 * Inline icons for the lifestyle page.
 *
 * Kept as components rather than files under public/assets so they inherit
 * `currentColor` and cost no extra requests — every one is a few hundred bytes
 * of single-colour line art.
 */

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

type IconProps = { className?: string };

export function ChevronRightIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      <path d="M6 3.5 10.5 8 6 12.5" {...strokeProps} />
    </svg>
  );
}

/** Double chevron (Figma Flag Icon) — Enter Now / filters CTA. */
export function ChevronsRightIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      <path d="M4.667 4.667 8 8l-3.333 3.333" {...strokeProps} strokeWidth={1.6} />
      <path d="M8.667 4.667 12 8l-3.333 3.333" {...strokeProps} strokeWidth={1.6} />
    </svg>
  );
}

export function ChevronLeftIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      <path d="M10 3.5 5.5 8 10 12.5" {...strokeProps} />
    </svg>
  );
}

export function ChevronDownIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      <path d="M3.5 6 8 10.5 12.5 6" {...strokeProps} />
    </svg>
  );
}

export function SearchIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" {...strokeProps} />
      <path d="m10.5 10.5 3 3" {...strokeProps} />
    </svg>
  );
}

export function SortIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M4 6h9M4 12h7M4 18h7M21 15l-3 3-3-3M18 18V6" {...strokeProps} strokeWidth={1.5} />
    </svg>
  );
}

export function FiltersIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M12 6a2 2 0 1 0 4 0 2 2 0 1 0-4 0M12 6H4M16 6h4M6 12a2 2 0 1 0 4 0 2 2 0 1 0-4 0M6 12H4M10 12h10M15 18a2 2 0 1 0 4 0 2 2 0 1 0-4 0M15 18H4M19 18h1"
        {...strokeProps}
        strokeWidth={1.5}
      />
    </svg>
  );
}

/**
 * The 2x2 grid that stands in for the Categories button on mobile. The 17.5
 * viewBox is the 16px icon box plus the 1.5 stroke it is centred on, so the
 * outer edges are not clipped.
 */
export function CategoryGridIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 17.5 17.5" className={className} aria-hidden="true">
      <path d="M0.75 0.75h6v6h-6z" {...strokeProps} strokeWidth={1.5} />
      <path d="M10.75 0.75h6v6h-6z" {...strokeProps} strokeWidth={1.5} />
      <path d="M0.75 10.75h6v6h-6z" {...strokeProps} strokeWidth={1.5} />
      <path d="M10.75 10.75h6v6h-6z" {...strokeProps} strokeWidth={1.5} />
    </svg>
  );
}

export function CartIcon({ className }: IconProps) {
  // Figma Icon / Vector (BOTB-updates-2026 · Product Card cart button)
  return (
    <svg viewBox="0 0 14.8333 16.5" className={className} aria-hidden="true" fill="none">
      <path
        d="M2.41667 12.4167C2.8587 12.4167 3.28262 12.5923 3.59518 12.9048C3.90774 13.2174 4.08334 13.6413 4.08334 14.0833C4.08334 14.5254 3.90774 14.9493 3.59518 15.2618C3.28262 15.5744 2.8587 15.75 2.41667 15.75C1.97464 15.75 1.55072 15.5744 1.23816 15.2618C0.9256 14.9493 0.75 14.5254 0.75 14.0833C0.75 13.6413 0.9256 13.2174 1.23816 12.9048C1.55072 12.5923 1.97464 12.4167 2.41667 12.4167ZM2.41667 12.4167H11.5833M2.41667 12.4167V0.75H0.75M11.5833 12.4167C12.0253 12.4167 12.4493 12.5923 12.7618 12.9048C13.0744 13.2174 13.25 13.6413 13.25 14.0833C13.25 14.5254 13.0744 14.9493 12.7618 15.2618C12.4493 15.5744 12.0253 15.75 11.5833 15.75C11.1413 15.75 10.7174 15.5744 10.4048 15.2618C10.0922 14.9493 9.91672 14.5254 9.91672 14.0833C9.91672 13.6413 10.0922 13.2174 10.4048 12.9048C10.7174 12.5923 11.1413 12.4167 11.5833 12.4167ZM2.41667 2.41667L14.0833 3.25L13.25 9.08331H2.41667"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Radio for the mobile sort sheet. Figma only draws the empty state, so the
 * selected state is the conventional filled dot in the same currentColor.
 */
export function RadioIcon({ className, isSelected = false }: IconProps & { isSelected?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="9" {...strokeProps} strokeWidth={1.5} />
      {isSelected && <circle cx="12" cy="12" r="5" fill="currentColor" />}
    </svg>
  );
}

export function CloseIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M18 6L6 18M6 6L18 18" {...strokeProps} strokeWidth={1.5} />
    </svg>
  );
}

const categoryStroke = {
  fill: "none" as const,
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Figma Icon Cars — Filters drawer lobby (15390:20753). */
export function CarIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none">
      <path d="M21.999 14.752V18.252c0 .552-.448 1-1 1H17.999c-.552 0-1-.448-1-1V16.752" {...categoryStroke} />
      <path d="M6.999 16.752V18.252c0 .552-.448 1-1 1H2.999c-.552 0-1-.448-1-1V14.752" {...categoryStroke} />
      <path
        d="m6.998 16.75 1.7-2.125c.19-.237.477-.375.781-.375h5.039c.304 0 .591.138.781.375L16.998 16.75"
        {...categoryStroke}
      />
      <path d="m22 12.253-4 1M1.999 12.253l4 1M18.888 7.25H21M3 7.25h2.111" {...categoryStroke} />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.999 11.253v3.5A2 2 0 0 0 3.999 16.753h16a2 2 0 0 0 2-2v-3.5a2 2 0 0 0-2-2L16.999 10.253H6.999L3.999 9.253a2 2 0 0 0-2 2Z"
        {...categoryStroke}
      />
      <path
        d="m19.998 9.253-1.928-3.471A2.5 2.5 0 0 0 16.322 4.753H7.675a2.5 2.5 0 0 0-2.248 1.029L3.998 9.253"
        {...categoryStroke}
      />
    </svg>
  );
}

/** Figma Cash/Lifestyle — Filters drawer lobby (15390:20754). */
export function CashIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none">
      <path
        d="M18 16.003H19.5A2.5 2.5 0 0 0 22 13.503V6.503A2.5 2.5 0 0 0 19.5 4.003H8.5A2.5 2.5 0 0 0 6 6.503V8.003"
        {...categoryStroke}
      />
      <rect x="1.999" y="7.997" width="16" height="12" rx="2.5" {...categoryStroke} />
      <circle cx="9.999" cy="14" r="2" {...categoryStroke} />
      <path d="m15.006 10.995-.005.005-.005-.005.005-.005.005.005" {...categoryStroke} />
      <path d="m5.005 16.995-.005.005-.005-.005.005-.005.005.005" {...categoryStroke} />
    </svg>
  );
}

/** Figma Bikes/Lifestyle — Filters drawer lobby (15390:20755). */
export function BikeIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none">
      <path
        d="M2.878 18.124a4.002 4.002 0 0 1 0-5.657 4.002 4.002 0 0 1 5.657 0 4.002 4.002 0 0 1 0 5.657 4.002 4.002 0 0 1-5.657 0Z"
        {...categoryStroke}
      />
      <path
        d="M16.877 18.124a4.002 4.002 0 0 1 0-5.657 4.002 4.002 0 0 1 5.657 0 4.002 4.002 0 0 1 0 5.657 4.002 4.002 0 0 1-5.657 0Z"
        {...categoryStroke}
      />
      <path d="M6 10.003h10.5L12.5 14.003h-5L11.5 10.003" {...categoryStroke} />
      <path d="M12.998 6h2l1.5 3 2 4" {...categoryStroke} />
    </svg>
  );
}

/**
 * Figma Watches/Lifestyle — Filters drawer lobby (15390:20757).
 * Leaf is ~19.5px inside a 24px box (12.5% inset on each side).
 */
export function WatchIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none">
      <g transform="translate(2.25 2.249)">
        <path d="m8.219 8.421 1.657 1.657 2.098-.508" {...categoryStroke} />
        <path
          d="m3.977 11.391-3.013 3.013a1 1 0 0 0 0 1.414l3.101 3.101a1 1 0 0 0 1.414 0l3.004-3.004"
          {...categoryStroke}
        />
        <path
          d="m11.389 3.977 3.013-3.013a1 1 0 0 1 1.414 0l3.101 3.101a1 1 0 0 1 0 1.414L15.523 8.111"
          {...categoryStroke}
        />
        <circle cx="9.751" cy="9.749" r="6" {...categoryStroke} />
      </g>
    </svg>
  );
}

/** Figma Others/Lifestyle — beach umbrella + lounger (15390:20756). */
export function GiftIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none">
      <path d="M12.001 15V9" {...categoryStroke} />
      <path d="M9 9c0-3.3 1.3-6 3-6s3 2.7 3 6" {...categoryStroke} />
      <path d="m7.1 18-1.1 3M15.9 18 17 21" {...categoryStroke} />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.5 17.999H15.6c1.1 0 2.1-.4 3-1l1.8-1.4c.5-.4.7-1.1.5-1.7v0c-.3-1-1.5-1.3-2.3-.7L16.8 14.599c-.3.3-.8.4-1.2.4H4.5c-.8 0-1.5.7-1.5 1.5v0c0 .8.7 1.5 1.5 1.5Z"
        {...categoryStroke}
      />
      <path fillRule="evenodd" clipRule="evenodd" d="M4.001 9H20.001c0-3-4-6-8-6s-8 3-8 6Z" {...categoryStroke} />
    </svg>
  );
}

/**
 * Figma Arrow Icon on filter drawer rows (15390:20752).
 * Kept separate from ChevronRightIcon (16px / 1.6) used elsewhere.
 */
export function FiltersDrawerChevronIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none">
      <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ArrowRightIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M19 12H5M14 17L19 12M14 7L19 12" {...strokeProps} strokeWidth={1.5} />
    </svg>
  );
}

export function TicketIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <path
        d="M3 7.5a1.5 1.5 0 0 1 1.5-1.5h11A1.5 1.5 0 0 1 17 7.5v1a1.5 1.5 0 0 0 0 3v1a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 12.5v-1a1.5 1.5 0 0 0 0-3v-1Z"
        {...strokeProps}
      />
      <path d="M8.5 8.5v3M11.5 8.5v3" {...strokeProps} />
    </svg>
  );
}

export function TrophyIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <path d="M6.5 3.5h7v4a3.5 3.5 0 0 1-7 0v-4Z" {...strokeProps} />
      <path
        d="M6.5 4.5H4.5v1a2.5 2.5 0 0 0 2 2.4M13.5 4.5h2v1a2.5 2.5 0 0 1-2 2.4M10 11v3M7.5 16.5h5"
        {...strokeProps}
      />
    </svg>
  );
}

/** Category slug to icon, for the Categories dropdown and filters drawer. */
export const CATEGORY_ICONS: Record<string, (props: IconProps) => JSX.Element> = {
  lifestylecars: CarIcon,
  cash: CashIcon,
  bikes: BikeIcon,
  watches: WatchIcon,
  other: GiftIcon,
};
